import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Gear, Trash, Clock } from '@phosphor-icons/react';

export default function SettingsPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    authAxios().get('/admin/settings')
      .then(res => setRetentionDays(res.data.retention_days))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios().put('/admin/settings', { retention_days: parseInt(retentionDays) });
      toast.success('Ayarlar kaydedildi');
    } catch { toast.error('Kaydetme başarısız'); }
    finally { setSaving(false); }
  };

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const res = await authAxios().post('/admin/cleanup');
      toast.success(res.data.message);
    } catch { toast.error('Temizlik başarısız'); }
    finally { setCleaning(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center gap-3">
        <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/admin')} className="text-white hover:bg-[#2C2C2E] h-10 w-10 p-0">
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Gear weight="fill" className="w-5 h-5 text-[hsl(var(--primary))]" />
          <h1 className="font-heading text-lg text-white">AYARLAR</h1>
        </div>
      </header>

      <main className="p-4 max-w-xl mx-auto space-y-4">
        {loading ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : (
          <>
            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
                  <Clock weight="bold" className="w-4 h-4" /> VİDEO SAKLAMA SÜRESİ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="overline text-[hsl(var(--muted-foreground))] mb-1.5 block">Saklama Süresi (Gün)</label>
                  <Input
                    data-testid="retention-days-input"
                    type="number"
                    min={1}
                    max={365}
                    value={retentionDays}
                    onChange={e => setRetentionDays(e.target.value)}
                    className="h-12 bg-[#0A0A0A] border-[#2C2C2E] text-white"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    Bu süreden eski videolar otomatik temizleme sırasında silinir. İhbar meta verileri korunur.
                  </p>
                </div>
                <Button
                  data-testid="save-settings-button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-heading uppercase"
                >
                  {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#1C1C1E] border-[#2C2C2E]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading text-white flex items-center gap-2">
                  <Trash weight="bold" className="w-4 h-4" /> MANUEL TEMİZLİK
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                  Saklama süresi geçmiş videoları şimdi temizleyin.
                </p>
                <Button
                  data-testid="cleanup-button"
                  onClick={handleCleanup}
                  disabled={cleaning}
                  variant="outline"
                  className="w-full h-12 border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] font-heading uppercase"
                >
                  {cleaning ? 'Temizleniyor...' : 'Eski Videoları Temizle'}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
